import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { withStore } from '../../../store/rootStore';
import { MessagingChannelFeature } from '@whatsper/texterchat-common';
import { has } from 'lodash';
import Compose from '../Compose';
import Controls from './Controls';
import Preview from './Preview';
import ModalWithoutBottom from '../common/ModalWithoutBottom';
import useCheckIfMobile from '../../../helpers/hooks/useCheckIfMobile';
import { withReplyQuote } from '../helpers';
import { getFileIconByName } from '../../../helpers/fileIcon';
import s from './FilePreview.module.scss';
const FilePreview = ({ store, activeChat, composeProps }) => {
    var _a, _b, _c, _d;
    const [pageHeight, setPageHeight] = useState(0);
    const innerRef = useRef(null);
    const isMobile = useCheckIfMobile();
    const { filePreview, toggleFilePreview, filesData, theme, activeReplyMessage } = store.activeChatStore;
    const [selectedFile, setSelectedFile] = useState();
    const [composeKey, setComposeKey] = useState(0);
    const adapter = activeChat
        ? store.channels.getAccountAdapter({
            id: activeChat.channelInfo.accountId,
            name: activeChat.channelInfo.name,
        })
        : null;
    const mediaLimits = adapter === null || adapter === void 0 ? void 0 : adapter.messageLimits.media;
    const supportsReply = store.channels.hasFeatures([MessagingChannelFeature.outboundMessageQuote], adapter);
    const composeButtonRef = useRef(null);
    const onPDFLoadSuccess = () => {
        var _a;
        const containerHeight = (((_a = innerRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0) * 0.9 - 12;
        setPageHeight(containerHeight);
    };
    const onKeyDown = (e) => {
        var _a;
        if (!filePreview)
            return;
        if (e.key === 'Escape') {
            toggleFilePreview(false);
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            (_a = composeButtonRef.current) === null || _a === void 0 ? void 0 : _a.click();
        }
    };
    useEffect(() => {
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [filePreview]);
    useEffect(() => {
        if ((filesData === null || filesData === void 0 ? void 0 : filesData.files) && (filesData === null || filesData === void 0 ? void 0 : filesData.files.length) > 0) {
            setSelectedFile(filesData.files[0]);
            setComposeKey((prev) => prev + 1);
        }
    }, [filesData]);
    if (!filePreview || !filesData)
        return null;
    const validSelectedFile = selectedFile && filesData.files.some((f) => { var _a, _b; return (((_a = f.file) === null || _a === void 0 ? void 0 : _a.uid) && f.file.uid === ((_b = selectedFile.file) === null || _b === void 0 ? void 0 : _b.uid)) || (f.fileId && f.fileId === selectedFile.fileId); }) ? selectedFile : filesData.files[0];
    const skipCaption = has(mediaLimits === null || mediaLimits === void 0 ? void 0 : mediaLimits[(validSelectedFile === null || validSelectedFile === void 0 ? void 0 : validSelectedFile.type) || ''], 'caption')
        ? !((_a = mediaLimits === null || mediaLimits === void 0 ? void 0 : mediaLimits[(validSelectedFile === null || validSelectedFile === void 0 ? void 0 : validSelectedFile.type) || '']) === null || _a === void 0 ? void 0 : _a.caption)
        : !((_b = mediaLimits === null || mediaLimits === void 0 ? void 0 : mediaLimits.document) === null || _b === void 0 ? void 0 : _b.caption);
    const disabledMessage = skipCaption ? 'captionNotAvailable' : null;
    const captionMaxLength = (_c = mediaLimits === null || mediaLimits === void 0 ? void 0 : mediaLimits[(validSelectedFile === null || validSelectedFile === void 0 ? void 0 : validSelectedFile.type) || '']) === null || _c === void 0 ? void 0 : _c.captionMaxLength;
    const caption = filesData.caption;
    const adapterAllowsCaptionFor = (type) => {
        var _a;
        if (!type)
            return false;
        const typeLimits = mediaLimits === null || mediaLimits === void 0 ? void 0 : mediaLimits[type];
        return has(typeLimits, 'caption') ? !!(typeLimits === null || typeLimits === void 0 ? void 0 : typeLimits.caption) : !!((_a = mediaLimits === null || mediaLimits === void 0 ? void 0 : mediaLimits.document) === null || _a === void 0 ? void 0 : _a.caption);
    };
    const sendFile = (message) => {
        if (filesData) {
            let caption = skipCaption ? '' : message;
            let messageQuotedId;
            if (activeReplyMessage) {
                if (supportsReply) {
                    messageQuotedId = activeReplyMessage.channelInfo.id;
                }
                else if (!skipCaption) {
                    caption = withReplyQuote(caption, activeReplyMessage);
                }
            }
            let captionTargetIndex = -1;
            if (caption) {
                for (let i = filesData.files.length - 1; i >= 0; i--) {
                    if (adapterAllowsCaptionFor(filesData.files[i].type)) {
                        captionTargetIndex = i;
                        break;
                    }
                }
            }
            const filesFormatted = filesData === null || filesData === void 0 ? void 0 : filesData.files.map((fileData, index) => {
                var _a;
                return {
                    file: fileData.file || null,
                    fileId: fileData.fileId,
                    type: fileData.type,
                    name: ((_a = fileData.file) === null || _a === void 0 ? void 0 : _a.name) || fileData.filename,
                    caption: index === captionTargetIndex ? caption : undefined,
                };
            });
            store.activeChatStore.sendMediaMessage(filesFormatted, messageQuotedId);
            composeProps.onChange('');
            toggleFilePreview(false);
        }
    };
    const onRemoveMedia = (fileUid) => {
        store.activeChatStore.removeFile(fileUid);
    };
    const onAddMedia = (files) => {
        store.activeChatStore.addFiles(files);
    };
    const renderFileIcon = (name, contentType) => {
        const Icon = getFileIconByName(name, contentType);
        return React.createElement(Icon, null);
    };
    return (React.createElement(ModalWithoutBottom, { setModalVisible: () => toggleFilePreview(false), titleTextKey: 'filePreviewTitle', titleTextValues: { count: (filesData === null || filesData === void 0 ? void 0 : filesData.files.length) || '' }, height: isMobile ? '90vh' : '650px', width: isMobile ? undefined : 1200, visible: true },
        React.createElement("div", { className: s.container },
            React.createElement("div", { ref: innerRef, className: s.inner }, validSelectedFile && (React.createElement(Preview, { file: validSelectedFile.file, fileId: validSelectedFile.fileId, filename: validSelectedFile.filename, type: validSelectedFile.type, pageHeight: pageHeight, onPDFLoadSuccess: onPDFLoadSuccess, renderFileIcon: renderFileIcon }))),
            React.createElement(Controls, { files: filesData.files, selectedFileUid: ((_d = validSelectedFile === null || validSelectedFile === void 0 ? void 0 : validSelectedFile.file) === null || _d === void 0 ? void 0 : _d.uid) || (validSelectedFile === null || validSelectedFile === void 0 ? void 0 : validSelectedFile.fileId), onAdd: onAddMedia, onRemove: onRemoveMedia, onSelect: (fileData) => setSelectedFile(fileData), mediaLimits: mediaLimits, renderFileIcon: renderFileIcon }),
            !!activeChat && (React.createElement("div", { className: s.actionBar },
                React.createElement("div", { className: s.composeWrapper },
                    React.createElement(Compose, Object.assign({ key: composeKey }, composeProps, { sendTextMessageCB: sendFile, activeChat: activeChat, disabled: !!disabledMessage, disabledMessage: disabledMessage, initialText: caption, theme: theme, textMaxLength: captionMaxLength, buttonRef: composeButtonRef, maxRows: 6, onQuickReplySelected: () => { }, onFilesDrop: () => { }, currentChatId: null, previewMode: true }))))))));
};
export default withStore(observer(FilePreview));
